import * as React from "react";
import { AtLink } from "@atman/design-system";
import { AtMenuButton, YouBadge } from "../../";
import { BaseColumn } from "./BaseColumn";
import { DropdownItem } from "reactstrap";
import { ReactAppSettings, Role, UserInfoStore, UserState, UserStore } from "@atman/business";
import { User } from "@atman/business";

import moment from "moment";

export class UserDisplayNameColumn extends BaseColumn {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "lastName";
        this.text = "global.name".localize();
        this.formatter = (cell: string, row: User) => {
            let name = "";

            if (row.firstName && row.lastName) {
                name = `${row.firstName} ${row.lastName}`;
            } else if (row.firstName && !row.lastName) {
                name = row.firstName;
            } else if (!row.firstName && row.lastName) {
                name = row.lastName;
            } else {
                name = row.email;
            }

            return <span>{name}</span>;
        };
    }
}

export class EmailColumn extends BaseColumn {
    constructor(formatExtraData?: unknown, overflow: boolean = true) {
        super(formatExtraData);

        this.dataField = "email";
        this.text = "global.email".localize();
        this.align = "left";
        this.formatter = (cell) => (
            <span style={overflow ? { textOverflow: "ellipsis", overflow: "hidden", maxWidth: "10vw" } : undefined}>
                {cell}
            </span>
        );
    }
}

export class LanguageCodeColumn extends BaseColumn {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "languageCode";
        this.text = "global.language".localize();
        this.formatter = (cell: string) => {
            // TODO @charles 2021-06-15: Change to localizationStore
            const userLanguage = ReactAppSettings.appModel.availableLanguages.find((x) => x.name === cell);
            return <span>{userLanguage ? userLanguage.shortDisplay : "global.unknown".localize()}</span>;
        };
    }
}

export class UserRolesColumn extends BaseColumn {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "baseRole";
        this.text = "global.baseRole".localize();
        this.formatter = (cell: string, row: User) => {
            if (!cell) return <span></span>;

            return <span className="textWeightMedium">{`global.role.${Role[Role[cell]].toCamel()}`.localize()}</span>;
        };
    }
}

export class UserStatusColumn extends BaseColumn {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);

        this.dataField = "state";
        this.text = "global.status".localize();
        this.formatter = (cell: UserState) => {
            if (cell === null || cell === undefined) return <span></span>;

            return <span>{`global.${UserState[cell].toCamel()}`.localize()}</span>;
        };
    }
}

export class UserButtonsColumn extends BaseColumn {
    constructor(
        openModalAction: (id: string) => void,
        secondaryModalAction: (id: string) => void,
        userInfoStore: UserInfoStore,
        userStore: UserStore,
        isDelegatedForClient: boolean,
        userClientsDelegationUpdateClick: ((id: string) => void) | null = null,
        isInEntityManagementPage: boolean,
    ) {
        super();

        this.dataField = "id";
        this.setAsButtonColumn();

        this.formatter = (cell: string, row: User) => {
            if (cell === userInfoStore.userId) {
                return <YouBadge showTooltip={true} />;
            }

            return (
                <span className="actions" style={{ display: "flex", alignItems: "center" }}>
                    {isDelegatedForClient && (
                        <AtLink color={"secondary"} onClick={() => openModalAction(cell)} style={{ marginRight: 16 }}>
                            {"global.edit".localize()}
                        </AtLink>
                    )}
                    <AtMenuButton size="md">
                        {userClientsDelegationUpdateClick && (
                            <DropdownItem onClick={() => userClientsDelegationUpdateClick(cell)}>
                                {"partnerApp.updateUserClientsDelegation".localize()}
                            </DropdownItem>
                        )}
                        {row.state === UserState.Active ? (
                            <DropdownItem onClick={() => userStore.deactivateUser(cell, isInEntityManagementPage)}>
                                {"global.buttons.labels.deactivate".localize()}
                            </DropdownItem>
                        ) : (
                            <DropdownItem onClick={() => userStore.reactivateUser(cell, isInEntityManagementPage)}>
                                {"global.buttons.labels.reactivate".localize()}
                            </DropdownItem>
                        )}
                        <DropdownItem onClick={() => secondaryModalAction(cell)}>
                            {"global.buttons.labels.delete".localize()}
                        </DropdownItem>
                    </AtMenuButton>
                </span>
            );
        };
    }
}

export class LastSeenColumn extends BaseColumn {
    constructor(formatExtraData?: unknown) {
        super(formatExtraData);
        this.dataField = "lastSeen";
        this.text = "global.lastSeen".localize();
        this.formatter = (cell: string) => {
            if (!cell) {
                return null;
            }

            return <span>{moment.parseZone(cell).fromNow()}</span>;
        };
    }
}
